import React from 'react';
import { IWebformProps } from './interfaces';
import { WebformView } from './webform-view';
import { TestApi,
         ITestResponseBody,
         IncidentApi,
         ITab,
         NewDataLoadHandler,
         UserApi,
         IUserElement,
         StaticApi,
         ITabField,
         VisibilityUtils,
         IFieldData,
         IVisibilityRulesData,
         MainHeader,
         FileApi,
         ITransitionFileResponse,
         formatFormDataForSubmit,
         dispatchGrowl,
         GROWL_TYPE,
         GROWL_HEADERS,
         GROWL_MESSAGES,
         ICreateIncidentQueryParams,
         convertDateToUTCString,
         FILES,
         IFieldValueObject,
         ERROR_CONSOLE_MSG,
} from '@logicmanager/common';
import { autobind } from 'core-decorators';
import map from 'lodash/map';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import './webform.less';
import { formHasBeenEdited } from './webform-utils';
import { WEBFORM_TEXT, GROWL_MSG_SUBMITTED } from '../../constants';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Prompt } from 'react-router';
import { Icon } from 'semantic-ui-react';
import { IFieldValueEditable, IFieldsMeta, STATUS } from '../../interfaces';
import { getValidatedFields } from '../../utils';
import isNil from 'lodash/isNil';

interface IInternalWebformState {
  localTimezoneFeatureFlagEnabled: boolean
}

export class InternalWebform extends React.PureComponent<IWebformProps> {
  private dataLoadHandler: any;
  private VISIBILITY_DEBOUNCE_TIME: number = 175;
  private fieldsToLoad: ITabField[] = [];
  public FILE_KEY: string = 'file';
  public NO_RESOURCE_TYPE: number = -1;
  public state: IInternalWebformState = {
    localTimezoneFeatureFlagEnabled: false
  };
  constructor(props: IWebformProps) {
    super(props);
    this.dataLoadHandler =
          new NewDataLoadHandler({
            addFieldMeta: this.props.updateFields,
            addFieldValues: this.props.updateValues,
            hasVisiRules: this.hasVisibilityRules,
            hasStates: this.loadStates,
            hasCountries: this.loadCountries,
            addIncompleteFields: null
          });
    this.getVisibilityRules = debounce(this.getVisibilityRules, this.VISIBILITY_DEBOUNCE_TIME);
  }

  public async componentDidMount(): Promise<void> {
    this.props.fetchingRequest();
    try {
      await Promise.all([
        this.loadAllGroupUsers(),
        UserApi.listAll().request()
          .then((users: IUserElement[]) => {
            this.props.addUsers(users);
          }),
        TestApi.getTestById(this.props.testId).request()
          .then((test: ITestResponseBody) => {
            this.props.addFormTitle(`New ${test.name}`);
            this.state.localTimezoneFeatureFlagEnabled = test.localTimezoneFeatureFlagEnabled;
        }),
        IncidentApi.getFieldsByTestId({testId: this.props.testId}).request()
          .then((tabs: ITab[]) => tabs.forEach(this.loadTabFields))
      ]);
      this.dataLoadHandler.handleLoadFields(this.fieldsToLoad);
      if (this.props.ui.hasVisibilityRules) {
        this.getVisibilityRules();
      }
      this.props.fetchingSuccess();
    } catch (err) {
      console.error(err);
      this.props.fetchingFailure();
    }
    window.addEventListener('beforeunload', this.blockExternalNavigation);
  }

  public componentWillUnmount(): void {
    window.removeEventListener('beforeunload', this.blockExternalNavigation);
  }

  @autobind
  public async loadAllGroupUsers(): Promise<void> {
    this.props.addGroupUsers(await UserApi.getGroupUsersExternal().request());
  }

  @autobind
  public async getVisibilityRules(): Promise<void> {
    const payload: IFieldData[] = VisibilityUtils.formatDataForVisibilityRules(this.props.values.fields);
    const rules: IVisibilityRulesData[] =
          await TestApi.saveVisibilityRules({testId: this.props.testId}, payload).request();
    rules.forEach((rule: IVisibilityRulesData) => {
      if (this.props.values.fields[rule.id] && this.props.values.fields[rule.id].visible !== rule.visible) {
        this.props.updateValue(rule.id.toString(), {
          visible: rule.visible
        });
      }
    });
  }

  @autobind
  public blockExternalNavigation(e: Event): string {
    if (formHasBeenEdited(this.props.values.fields, this.props.meta.fields as ITabField[])) {
      e.preventDefault();
      // Internet Explorer needs text from this property to display custom message
      e.returnValue = WEBFORM_TEXT.NAVIGATION_PROMPT as any;
      return;
    }
  }

  @autobind
  public blockInternalNavigation(location: any): boolean | string {
    // if store.ui.isFetching is true (happens during submission), or form has not been edited, don't block navigation
    return ((this.props.ui.isFetching && this.props.ui.status === STATUS.Fetching)
      || !formHasBeenEdited(this.props.values.fields, this.props.meta.fields as ITabField[]))
        ? true
        : WEBFORM_TEXT.NAVIGATION_PROMPT;
  }

  @autobind
  public componentDidUpdate(prevProps: any): void {
    if (this.props.ui.hasVisibilityRules) {
      const prevPayload: IFieldData[] = VisibilityUtils.formatDataForVisibilityRules(this.props.values.fields);
      const currPayload: IFieldData[] = VisibilityUtils.formatDataForVisibilityRules(prevProps.values.fields);
      if (!isEqual(prevPayload, currPayload)) {
        this.getVisibilityRules();
      }
    }
  }

  @autobind
  public loadTabFields(tab: ITab): void {
    this.props.addTab({
      ...tab,
      fields: map(tab.fields as ITabField[], 'id') as string[]
    });
    this.props.setTabStatus(tab.id, tab.opened);
    this.fieldsToLoad = this.fieldsToLoad.concat(tab.fields as ITabField[]);
  }

  @autobind
  public async loadStates(needToLoad: boolean): Promise<void> {
    if (needToLoad && this.props.meta.states.length < 1) {
      this.props.addStates(await StaticApi.getStateList().request());
    }
  }

  @autobind
  public async loadCountries(needToLoad: boolean): Promise<void> {
    if (needToLoad && this.props.meta.countries.length < 1) {
      this.props.addCountries(await StaticApi.getCountryList().request());
    }
  }

  @autobind
  public hasVisibilityRules(hasVisiRules: boolean): void {
    if (!hasVisiRules) {
      this.props.noVisibilityRules();
    }
  }

  @autobind
  public downloadFile(fieldId: string): void {
    FileApi.getAttachmentViaTicket({
      resourceTypeId: this.NO_RESOURCE_TYPE,
      resourceId: this.props.testId,
      fieldId: parseInt(fieldId, 10)});
  }

  @autobind
  public async saveTemplateFile(file: File, id: string): Promise<void> {
    const requestBody: FormData = new FormData();
    requestBody.append(this.FILE_KEY, file);
    const fileInfo: ITransitionFileResponse
        = await FileApi.createTransitionFile(requestBody);
    this.props.updateValue(id, {
      value: fileInfo.fileName,
      valid: true,
      extra: fileInfo.fileIdentity
    });
  }

  @autobind
  public async saveTemplateFiles(files: File[], id: string): Promise<void> {
    try {
      const fieldValue: IFieldValueObject = this.props.values.fields[id];
      const requestBody: FormData = new FormData();
      files.forEach((file: File) => {
        requestBody.append(FILES, file, file.name);
      });
      const filesInfo: ITransitionFileResponse[] = await FileApi.createTransitionFiles(requestBody);

      const oldValue: string = fieldValue.value as string;

      const newValueArr: string[] = [];

      const oldExtra: string = fieldValue.extra;
      const newExtraArr: string[] = [];

      filesInfo.forEach((response: ITransitionFileResponse) => {
        newValueArr.push(response.fileName);
        newExtraArr.push(response.fileIdentity);
      });

      const newExtra: string = newExtraArr.join(',');
      const newValue: string = newValueArr.join(',');

      const extra: string = !isNil(oldExtra) && oldExtra.trim().length > 0
        ? oldExtra.concat(',', newExtra)
        : newExtra;

      const value: string = !isNil(oldValue) && oldValue.trim().length > 0
        ? oldValue.concat(',', newValue)
        : newValue;

      this.props.updateValue(id, {
        value,
        valid: true,
        extra,
      });
    } catch (e) {
      console.error(ERROR_CONSOLE_MSG.SAVE_TEMPLATE_FILES, e);
      throw e;
    }
  }

  @autobind
  public async handleSubmit(): Promise<void> {
    const fieldsMeta: IFieldsMeta = this.props.meta.fields as IFieldsMeta;
    const fieldValuesToSubmit: IFieldValueEditable =
      getValidatedFields(fieldsMeta, { ...this.props.values.fields }, this.props.updateValue, this.props.setTabStatus);
    if (!fieldValuesToSubmit) {
      return;
    }
    const body: IFieldData[] = formatFormDataForSubmit(fieldValuesToSubmit);
    const reportedOn: string =  this.state.localTimezoneFeatureFlagEnabled
      ? convertDateToUTCString(new Date(new Date().setHours(0, 0, 0, 0)))
      : '';
    const query: ICreateIncidentQueryParams = this.props.entityId && this.props.entityTypeId
      ? {
        refEntityId: this.props.entityId,
        refEntityType: this.props.entityTypeId,
        reportedOn
      }
      : {
        reportedOn
      };
    this.props.fetchingRequest();
    try {
      await IncidentApi.createNewIncidentByTestId(this.props.testId, body, query).request();
      const submit: boolean = !!this.props.entityTypeId && !!this.props.entityId;
      if (this.props.onCloseModal) {
        this.props.onCloseModal(submit);
      }
      dispatchGrowl(GROWL_TYPE.SUCCESS, GROWL_HEADERS.SUCCESS, GROWL_MSG_SUBMITTED);
    } catch (err) {
      console.error(err);
      // need to set ui state using props.fetchingSuccess()
      // as WebformView tries to redirect to /error if we use props.fetchingFailure()
      this.props.fetchingSuccess();
      dispatchGrowl(GROWL_TYPE.ERROR, GROWL_HEADERS.ERROR, GROWL_MESSAGES.ERROR);
    }
  }

  @autobind
  public getCloseIcon(modal: boolean): JSX.Element {
    return modal ? <Icon name="close" onClick={this.props.onCloseModal} /> : null;
  }

  public render(): JSX.Element {
    const formTitle: string = this.props.meta.title || WEBFORM_TEXT.DEFAULT_INCIDENT_NAME;
    return (
      <div className="internal-webform-container">
        <MainHeader heading={formTitle} childrenPosition="end">
          {this.getCloseIcon(this.props.modal)}
        </MainHeader>
        <div className="internal-webform">
          <WebformView
            {...this.props}
            handleSubmit={this.handleSubmit}
            handleCaptchaVerify={null}
            handleCaptchaExpiration={null}
            formTitle={this.props.meta.title}
            downloadFile={this.downloadFile}
            saveTemplateFile={this.saveTemplateFile}
            saveTemplateFiles={this.saveTemplateFiles}
            groupUsers={this.props.groupUsers}
          />
          <Prompt
            message={this.blockInternalNavigation}
          />
        </div>
      </div>
    );
  }
}
